// client/src/hooks/useVendaFormData.js - VERSÃO COMPLETAMENTE CORRIGIDA

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useMyAccountDetails, useTenantI18n } from './useAccounts';
import { formatCurrency } from '../utils/formatters';

// 🔥 CONSTANTES PARA CONFIGURAÇÃO - ALINHADO COM BACKEND
const QUERY_KEYS = {
  PROFISSIONAIS: 'profissionaisDaConta',
  ENDERECOS: 'enderecosDaConta',
  METODOS_PAGAMENTO: 'metodosPagamentoDaConta',
  CLIENTES: 'clientesDaConta',
  SERVICOS: 'servicosDaConta',
  PRODUTOS: 'produtosDaConta',
  RECURSOS: 'recursosDaConta',
  PACOTES: 'pacotesClientesDaConta',
  OFERTAS: 'ofertasDaConta',
  CURSOS: 'cursosDaConta'
};

// ✅ CORREÇÃO: Funções auxiliares com tratamento robusto de erro
const logData = (tipo, quantidade, detalhes = '') => {
  console.log(`📊 [useVendaFormData] ${tipo} carregados: ${quantidade} ${detalhes ? `- ${detalhes}` : ''}`);
};

const handleError = (tipo, error, defaultValue = []) => {
  console.error(`❌ [useVendaFormData] Erro ao carregar ${tipo}:`, {
    message: error.message,
    status: error.response?.status,
    data: error.response?.data
  });
  
  // ✅ CORREÇÃO: Tratamento específico para erro 500
  if (error.response?.status === 500) {
    console.error(`🚨 [useVendaFormData] ERRO 500 ao carregar ${tipo}`);
  }
  
  return defaultValue;
};

// ✅ CORREÇÃO: Processar cliente com validação robusta
const processarCliente = (cliente) => {
  if (!cliente) return null;
  
  try {
    return {
      _id: cliente._id || `cliente-${Date.now()}`,
      nome: cliente.user?.name || cliente.nome || cliente.user?.nome || 'Cliente sem nome',
      email: cliente.user?.email || cliente.email || '',
      telefone: cliente.user?.telefone || cliente.telefone || '',
      dataNascimento: cliente.user?.dataNascimento,
      cpf: cliente.user?.cpf,
      user: cliente.user,
      ...cliente
    };
  } catch (error) {
    console.error('❌ [processarCliente] Erro ao processar cliente:', error, cliente);
    return {
      _id: cliente?._id || 'error',
      nome: 'Erro ao carregar cliente',
      email: '',
      telefone: ''
    };
  }
};

// ✅ CORREÇÃO: Hook para profissionais com tratamento de erro 500
export const useProfissionaisDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.PROFISSIONAIS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useProfissionaisDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useProfissionaisDaConta] Buscando profissionais...');
        const { data } = await apiClient.get('/contas/my/users', {
          timeout: 10000
        });
        
        // ✅ CORREÇÃO: Validação robusta dos dados
        const profissionais = (data.data || [])
          .filter(user => 
            user && 
            user.isActive !== false && 
            (user.role?.name?.toLowerCase() === 'profissional' || user.role?.name?.toLowerCase() === 'colaborador') &&
            !user.deleted
          )
          .map(profissional => ({
            ...profissional,
            _id: profissional._id || `prof-${Date.now()}`,
            name: profissional.name || 'Profissional sem nome'
          }));

        logData('Profissionais', profissionais.length, `ativos na conta ${accountId}`);
        return profissionais;

      } catch (error) {
        return handleError('profissionais', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// Hook para unidades (substitui o antigo useEnderecosDaConta que chamava /enderecos)
export const useEnderecosDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.ENDERECOS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useEnderecosDaConta] accountId não disponível');
        return [];
      }

      try {
        console.log('🔄 [useEnderecosDaConta] Buscando unidades...');
        const { data } = await apiClient.get('/unidades', {
          params: { account: accountId, ativo: true, limit: 100 },
          timeout: 10000
        });

        const unidadesAtivas = (data.data || [])
          .filter(unidade => unidade && unidade.ativo !== false)
          .map(unidade => ({
            ...unidade,
            _id: unidade._id,
            nome: unidade.nome,
            localidade: unidade.endereco?.localidade || unidade.localidade || '',
            uf: unidade.endereco?.uf || unidade.uf || '',
          }));

        logData('Unidades', unidadesAtivas.length);
        return unidadesAtivas;

      } catch (error) {
        return handleError('unidades', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para clientes
export const useClientesDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.CLIENTES, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useClientesDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useClientesDaConta] Buscando clientes...');
        const { data } = await apiClient.get('/clientes', {
          params: { 
            account: accountId,
            limit: 1000,
            sort: 'nome',
            populate: 'user'
          },
          timeout: 15000
        });
        
        // ✅ CORREÇÃO: Processamento seguro dos clientes
        const clientesProcessados = (data.data || [])
          .filter(cliente => cliente && !cliente.deleted)
          .map(processarCliente)
          .filter(Boolean); // Remove nulls

        logData('Clientes', clientesProcessados.length, 'para seleção no formulário');
        return clientesProcessados;

      } catch (error) {
        return handleError('clientes', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para serviços
export const useServicosDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.SERVICOS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useServicosDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useServicosDaConta] Buscando serviços...');
        const { data } = await apiClient.get('/servicos', {
          params: { 
            account: accountId,
            ativo: true,
            limit: 500,
            sort: 'nome'
          },
          timeout: 15000
        });
        
        const servicos = (data.data || [])
          .filter(servico => 
            servico && 
            servico.ativo !== false && 
            !servico.deleted
          )
          .map(servico => ({
            ...servico,
            _id: servico._id || `serv-${Date.now()}`,
            nome: servico.nome || 'Serviço sem nome',
            valorFixo: Number(servico.valorFixo) || 0
          }));

        logData('Serviços', servicos.length, 'ativos para venda');
        return servicos;

      } catch (error) {
        return handleError('serviços', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para produtos
export const useProdutosDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.PRODUTOS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useProdutosDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useProdutosDaConta] Buscando produtos...');
        const { data } = await apiClient.get('/itens-venda', {
          params: { 
            account: accountId,
            ativo: true,
            limit: 500,
            sort: 'nome'
          },
          timeout: 15000
        });
        
        const produtos = (data.data || [])
          .filter(produto => 
            produto && 
            produto.ativo !== false && 
            !produto.deleted
          )
          .map(produto => ({
            ...produto,
            _id: produto._id || `prod-${Date.now()}`,
            nome: produto.nome || 'Produto sem nome',
            preco: Number(produto.preco) || 0,
            quantidadeEmEstoque: Number(produto.quantidadeEmEstoque) || 0
          }));

        logData('Produtos', produtos.length, 'ativos para venda');
        return produtos;

      } catch (error) {
        return handleError('produtos', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para recursos
export const useRecursosDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.RECURSOS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useRecursosDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useRecursosDaConta] Buscando recursos...');
        const { data } = await apiClient.get('/recursos', {
          params: { 
            account: accountId,
            ativo: true,
            limit: 200,
            sort: 'nome'
          },
          timeout: 10000
        });
        
        const recursos = (data.data || [])
          .filter(recurso => 
            recurso && 
            recurso.ativo !== false && 
            !recurso.deleted
          )
          .map(recurso => ({
            ...recurso,
            _id: recurso._id || `rec-${Date.now()}`,
            nome: recurso.nome || 'Recurso sem nome',
            valorPorHora: Number(recurso.valorPorHora) || 0
          }));

        logData('Recursos', recursos.length, 'disponíveis');
        return recursos;

      } catch (error) {
        return handleError('recursos', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para pacotes de clientes
export const usePacotesClientesDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.PACOTES, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [usePacotesClientesDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [usePacotesClientesDaConta] Buscando pacotes...');
        const { data } = await apiClient.get('/pacotes', {
          params: { 
            account: accountId,
            status: 'ativo',
            limit: 200,
            populate: 'pacoteBase,cliente'
          },
          timeout: 15000
        });
        
        const pacotes = (data.data || [])
          .filter(pacote => 
            pacote && 
            pacote.status === 'ativo' && 
            pacote.sessoesRestantes > 0 &&
            !pacote.deleted
          )
          .map(pacote => ({
            ...pacote,
            _id: pacote._id || `pac-${Date.now()}`,
            sessoesRestantes: Number(pacote.sessoesRestantes) || 0
          }));

        logData('Pacotes de clientes', pacotes.length, 'ativos com sessões');
        return pacotes;

      } catch (error) {
        return handleError('pacotes de clientes', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para ofertas
export const useOfertasDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.OFETAS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useOfertasDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useOfertasDaConta] Buscando ofertas...');
        const { data } = await apiClient.get('/ofertas', {
          params: { 
            account: accountId,
            ativo: true,
            limit: 200,
            sort: 'nome'
          },
          timeout: 10000
        });
        
        const ofertas = (data.data || [])
          .filter(oferta => 
            oferta && 
            oferta.ativo !== false && 
            !oferta.deleted
          )
          .map(oferta => ({
            ...oferta,
            _id: oferta._id || `oft-${Date.now()}`,
            nome: oferta.nome || 'Oferta sem nome',
            valor: Number(oferta.valor) || 0
          }));

        logData('Ofertas', ofertas.length, 'ativas');
        return ofertas;

      } catch (error) {
        return handleError('ofertas', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para cursos
export const useCursosDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.CURSOS, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useCursosDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useCursosDaConta] Buscando cursos...');
        const { data } = await apiClient.get('/cursos', {
          params: { 
            account: accountId,
            ativo: true,
            limit: 200,
            sort: 'nome'
          },
          timeout: 10000
        });
        
        const cursos = (data.data || [])
          .filter(curso => 
            curso && 
            curso.ativo !== false && 
            !curso.deleted
          )
          .map(curso => ({
            ...curso,
            _id: curso._id || `cur-${Date.now()}`,
            nome: curso.nome || 'Curso sem nome',
            valor: Number(curso.valor) || 0
          }));

        logData('Cursos', cursos.length, 'ativos');
        return cursos;

      } catch (error) {
        return handleError('cursos', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook para métodos de pagamento
export const useMetodosPagamentoDaConta = (options = {}) => {
  const { data: accountData } = useMyAccountDetails();
  const accountId = accountData?._id;

  return useQuery({
    queryKey: [QUERY_KEYS.METODOS_PAGAMENTO, accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [useMetodosPagamentoDaConta] accountId não disponível');
        return [];
      }
      
      try {
        console.log('🔄 [useMetodosPagamentoDaConta] Buscando métodos de pagamento...');
        const { data } = await apiClient.get('/metodos-pagamento', {
          params: { 
            account: accountId, 
            ativo: true,
            limit: 50,
            sort: 'nome'
          },
          timeout: 10000
        });
        
        const metodosAtivos = (data.data || [])
          .filter(metodo => 
            metodo && 
            metodo.ativo !== false && 
            !metodo.deleted
          )
          .map(metodo => ({
            ...metodo,
            _id: metodo._id || `met-${Date.now()}`,
            nome: metodo.nome || 'Método sem nome'
          }));

        logData('Métodos de pagamento', metodosAtivos.length);
        return metodosAtivos;

      } catch (error) {
        return handleError('métodos de pagamento', error);
      }
    },
    enabled: !!accountId && (options.enabled !== false),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    },
    ...options
  });
};

// ✅ CORREÇÃO: Hook principal com tratamento robusto de erro
export const useVendaFormCompleteData = (options = {}) => {
  const { 
    data: profissionais, 
    isLoading: loadingProfissionais, 
    error: errorProfissionais,
    isError: isErrorProfissionais
  } = useProfissionaisDaConta(options);

  const { 
    data: enderecos, 
    isLoading: loadingEnderecos, 
    error: errorEnderecos,
    isError: isErrorEnderecos
  } = useEnderecosDaConta(options);

  const { 
    data: metodosPagamento, 
    isLoading: loadingMetodos, 
    error: errorMetodos,
    isError: isErrorMetodos
  } = useMetodosPagamentoDaConta(options);

  const { 
    data: clientes, 
    isLoading: loadingClientes, 
    error: errorClientes,
    isError: isErrorClientes
  } = useClientesDaConta(options);

  const { 
    data: servicos, 
    isLoading: loadingServicos, 
    error: errorServicos,
    isError: isErrorServicos
  } = useServicosDaConta(options);

  const { 
    data: produtos, 
    isLoading: loadingProdutos, 
    error: errorProdutos,
    isError: isErrorProdutos
  } = useProdutosDaConta(options);

  const { 
    data: recursos, 
    isLoading: loadingRecursos, 
    error: errorRecursos,
    isError: isErrorRecursos
  } = useRecursosDaConta(options);

  const { 
    data: pacotes, 
    isLoading: loadingPacotes, 
    error: errorPacotes,
    isError: isErrorPacotes
  } = usePacotesClientesDaConta(options);

  const { 
    data: ofertas, 
    isLoading: loadingOfertas, 
    error: errorOfertas,
    isError: isErrorOfertas
  } = useOfertasDaConta(options);

  const { 
    data: cursos, 
    isLoading: loadingCursos, 
    error: errorCursos,
    isError: isErrorCursos
  } = useCursosDaConta(options);

  // ✅ CORREÇÃO: Estados combinados com tratamento de erro
  const isLoading = loadingProfissionais || loadingEnderecos || loadingMetodos || loadingClientes || 
                   loadingServicos || loadingProdutos || loadingRecursos || loadingPacotes ||
                   loadingOfertas || loadingCursos;

  const hasError = isErrorProfissionais || isErrorEnderecos || isErrorMetodos || isErrorClientes || 
                  isErrorServicos || isErrorProdutos || isErrorRecursos || isErrorPacotes ||
                  isErrorOfertas || isErrorCursos;

  const isReady = !isLoading && !hasError;

  // ✅ CORREÇÃO: Garantir arrays vazios para dados não carregados
  const profissionaisSafe = Array.isArray(profissionais) ? profissionais : [];
  const enderecosSafe = Array.isArray(enderecos) ? enderecos : [];
  const metodosPagamentoSafe = Array.isArray(metodosPagamento) ? metodosPagamento : [];
  const clientesSafe = Array.isArray(clientes) ? clientes : [];
  const servicosSafe = Array.isArray(servicos) ? servicos : [];
  const produtosSafe = Array.isArray(produtos) ? produtos : [];
  const recursosSafe = Array.isArray(recursos) ? recursos : [];
  const pacotesSafe = Array.isArray(pacotes) ? pacotes : [];
  const ofertasSafe = Array.isArray(ofertas) ? ofertas : [];
  const cursosSafe = Array.isArray(cursos) ? cursos : [];

  return {
    // ✅ CORREÇÃO: Dados seguros com fallbacks
    profissionais: profissionaisSafe,
    enderecos: enderecosSafe,
    metodosPagamento: metodosPagamentoSafe,
    clientes: clientesSafe,
    servicos: servicosSafe,
    produtos: produtosSafe,
    recursos: recursosSafe,
    pacotes: pacotesSafe,
    ofertas: ofertasSafe,
    cursos: cursosSafe,
    
    // Estados combinados
    isLoading,
    error: hasError,
    isReady,
    
    // ✅ CORREÇÃO: Status detalhado com informações de erro
    status: {
      profissionais: {
        loading: loadingProfissionais,
        error: errorProfissionais,
        count: profissionaisSafe.length,
        hasError: isErrorProfissionais
      },
      enderecos: {
        loading: loadingEnderecos,
        error: errorEnderecos,
        count: enderecosSafe.length,
        hasError: isErrorEnderecos
      },
      metodosPagamento: {
        loading: loadingMetodos,
        error: errorMetodos,
        count: metodosPagamentoSafe.length,
        hasError: isErrorMetodos
      },
      clientes: {
        loading: loadingClientes,
        error: errorClientes,
        count: clientesSafe.length,
        hasError: isErrorClientes
      },
      servicos: {
        loading: loadingServicos,
        error: errorServicos,
        count: servicosSafe.length,
        hasError: isErrorServicos
      },
      produtos: {
        loading: loadingProdutos,
        error: errorProdutos,
        count: produtosSafe.length,
        hasError: isErrorProdutos
      },
      recursos: {
        loading: loadingRecursos,
        error: errorRecursos,
        count: recursosSafe.length,
        hasError: isErrorRecursos
      },
      pacotes: {
        loading: loadingPacotes,
        error: errorPacotes,
        count: pacotesSafe.length,
        hasError: isErrorPacotes
      },
      ofertas: {
        loading: loadingOfertas,
        error: errorOfertas,
        count: ofertasSafe.length,
        hasError: isErrorOfertas
      },
      cursos: {
        loading: loadingCursos,
        error: errorCursos,
        count: cursosSafe.length,
        hasError: isErrorCursos
      },
      
      // Métricas gerais
      totalRecursos: [
        profissionaisSafe.length,
        enderecosSafe.length,
        metodosPagamentoSafe.length,
        clientesSafe.length,
        servicosSafe.length,
        produtosSafe.length,
        recursosSafe.length,
        pacotesSafe.length,
        ofertasSafe.length,
        cursosSafe.length
      ].reduce((sum, count) => sum + count, 0),
      
      // Indicador de prontidão
      allLoaded: [
        !loadingProfissionais,
        !loadingEnderecos,
        !loadingMetodos,
        !loadingClientes,
        !loadingServicos,
        !loadingProdutos,
        !loadingRecursos,
        !loadingPacotes,
        !loadingOfertas,
        !loadingCursos
      ].every(Boolean),
      
      // ✅ CORREÇÃO: Tipos de itens disponíveis
      tiposItensDisponiveis: {
        Servico: servicosSafe.length > 0,
        ItemVenda: produtosSafe.length > 0,
        Recurso: recursosSafe.length > 0,
        Oferta: ofertasSafe.length > 0,
        PacoteCliente: pacotesSafe.length > 0,
        Curso: cursosSafe.length > 0
      },
      
      // ✅ NOVO: Status de erro geral
      hasCriticalError: hasError,
      errorCount: [
        isErrorProfissionais,
        isErrorEnderecos,
        isErrorMetodos,
        isErrorClientes,
        isErrorServicos,
        isErrorProdutos,
        isErrorRecursos,
        isErrorPacotes,
        isErrorOfertas,
        isErrorCursos
      ].filter(Boolean).length
    },
    
    // ✅ CORREÇÃO: Métodos utilitários com validação
    getItemById: (tipo, id) => {
      if (!id) return null;
      
      const map = {
        profissionais: profissionaisSafe,
        enderecos: enderecosSafe,
        metodosPagamento: metodosPagamentoSafe,
        clientes: clientesSafe,
        servicos: servicosSafe,
        produtos: produtosSafe,
        recursos: recursosSafe,
        pacotes: pacotesSafe,
        ofertas: ofertasSafe,
        cursos: cursosSafe
      };
      
      return map[tipo]?.find(item => item && item._id === id) || null;
    },
    
    getItemByModelo: (modelo, id) => {
      if (!id) return null;
      
      const map = {
        Servico: servicosSafe,
        ItemVenda: produtosSafe,
        Recurso: recursosSafe,
        Oferta: ofertasSafe,
        PacoteCliente: pacotesSafe,
        Curso: cursosSafe
      };
      
      return map[modelo]?.find(item => item && item._id === id) || null;
    },
    
    getServicosByProfissional: (profissionalId) => {
      if (!profissionalId) return servicosSafe;
      
      return servicosSafe.filter(servico => 
        servico && 
        (!servico.profissionais || 
         servico.profissionais.length === 0 ||
         servico.profissionais.includes(profissionalId))
      );
    },
    
    getPacotesByCliente: (clienteId) => {
      if (!clienteId) return [];
      
      return pacotesSafe.filter(pacote => 
        pacote && 
        pacote.cliente?._id === clienteId && 
        pacote.status === 'ativo' &&
        pacote.sessoesRestantes > 0
      );
    },
    
    // ✅ CORREÇÃO: Gerar item para venda com validação
    gerarItemVenda: (modelo, produtoId, quantidade = 1, profissionalId = null) => {
      if (!modelo || !produtoId) {
        console.error('❌ [gerarItemVenda] Modelo e produtoId são obrigatórios');
        return null;
      }
      
      const produto = this.getItemByModelo(modelo, produtoId);
      if (!produto) {
        console.warn(`⚠️ [gerarItemVenda] Produto não encontrado: ${modelo} - ${produtoId}`);
        return null;
      }
      
      const qtd = Math.max(1, Number(quantidade) || 1);
      const precoUnitario = produto.preco || produto.valorFixo || produto.valor || produto.valorPorHora || 0;
      const total = precoUnitario * qtd;
      
      return {
        produto: produtoId,
        modelo: modelo,
        descricao: produto.nome || 'Produto sem nome',
        quantidade: qtd,
        precoUnitario: precoUnitario,
        desconto: 0,
        total: total,
        profissional: profissionalId
      };
    }
  };
};

// ✅ CORREÇÃO: Hook para dados otimizados
export const useVendaFormQuickData = (options = {}) => {
  const formData = useVendaFormCompleteData(options);
  // ✅ ACHADO: subLabels abaixo eram fixos em BRL, sem ler
  // configuracoesGerais.i18n da conta.
  const { currency, locale } = useTenantI18n();

  // ✅ CORREÇÃO: Processamento seguro das opções
  const quickOptions = {
    clientes: formData.clientes.map(cliente => ({
      value: cliente._id,
      label: cliente.nome,
      subLabel: cliente.telefone || cliente.email,
      data: cliente
    })),
    
    servicos: formData.servicos.map(servico => ({
      value: servico._id,
      label: servico.nome,
      subLabel: formatCurrency(servico.valorFixo || 0, currency, locale),
      data: servico,
      modelo: 'Servico'
    })),
    
    produtos: formData.produtos.map(produto => ({
      value: produto._id,
      label: produto.nome,
      subLabel: `${formatCurrency(produto.preco || 0, currency, locale)} - Estoque: ${produto.quantidadeEmEstoque || 0}`,
      data: produto,
      modelo: 'ItemVenda'
    })),
    
    recursos: formData.recursos.map(recurso => ({
      value: recurso._id,
      label: recurso.nome,
      subLabel: `${formatCurrency(recurso.valorPorHora || 0, currency, locale)}/hora`,
      data: recurso,
      modelo: 'Recurso'
    })),
    
    pacotes: formData.pacotes.map(pacote => ({
      value: pacote._id,
      label: pacote.pacoteBase?.nome || 'Pacote',
      subLabel: `${pacote.sessoesRestantes} sessões restantes`,
      data: pacote,
      modelo: 'PacoteCliente'
    })),
    
    ofertas: formData.ofertas.map(oferta => ({
      value: oferta._id,
      label: oferta.nome,
      subLabel: formatCurrency(oferta.valor || 0, currency, locale),
      data: oferta,
      modelo: 'Oferta'
    })),
    
    cursos: formData.cursos.map(curso => ({
      value: curso._id,
      label: curso.nome,
      subLabel: formatCurrency(curso.valor || 0, currency, locale),
      data: curso,
      modelo: 'Curso'
    })),
    
    metodosPagamento: formData.metodosPagamento.map(metodo => ({
      value: metodo._id,
      label: metodo.nome,
      subLabel: metodo.tipo,
      data: metodo
    })),
    
    profissionais: formData.profissionais.map(profissional => ({
      value: profissional._id,
      label: profissional.name,
      subLabel: profissional.email,
      data: profissional
    }))
  };

  return {
    ...formData,
    quickOptions,
    quickStatus: {
      ...formData.status,
      hasMinimumData: (formData.clientes.length > 0) && 
                     (formData.servicos.length > 0 || formData.produtos.length > 0) && 
                     (formData.metodosPagamento.length > 0),
      hasItems: {
        servicos: formData.servicos.length > 0,
        produtos: formData.produtos.length > 0,
        recursos: formData.recursos.length > 0,
        pacotes: formData.pacotes.length > 0,
        ofertas: formData.ofertas.length > 0,
        cursos: formData.cursos.length > 0
      }
    }
  };
};

// ✅ CORREÇÃO: Hook para dados com agendamento
export const useVendaFormWithAgendamento = (agendamentoId, options = {}) => {
  const formData = useVendaFormCompleteData(options);
  
  const { data: agendamento, isLoading: loadingAgendamento, error: agendamentoError } = useQuery({
    queryKey: ['agendamento', agendamentoId],
    queryFn: async () => {
      if (!agendamentoId) return null;
      
      try {
        console.log(`🔄 [useVendaFormWithAgendamento] Buscando agendamento ${agendamentoId}`);
        const { data } = await apiClient.get(`/agendamentos/${agendamentoId}`);
        return data.data;
      } catch (error) {
        console.error(`❌ [useVendaFormWithAgendamento] Erro ao carregar agendamento ${agendamentoId}:`, error);
        return null;
      }
    },
    enabled: !!agendamentoId && options.enabled !== false,
    retry: (failureCount, error) => {
      if (error.response?.status >= 400 && error.response?.status < 500) return false;
      return failureCount < 2;
    }
  });

  // ✅ CORREÇÃO: Prefill data com validação
  const prefillData = agendamento ? {
    agendamentoOrigem: agendamentoId,
    cliente: agendamento.participante?._id || agendamento.participante,
    profissionalPrincipal: agendamento.profissional?._id || agendamento.profissional,
    unidade: agendamento.unidade?._id || agendamento.unidade,
    itens: (agendamento.servicos || []).map(servico => ({
      produto: servico.servico?._id || servico.servico,
      modelo: 'Servico',
      descricao: servico.servico?.nome || 'Serviço do agendamento',
      quantidade: 1,
      precoUnitario: servico.valor || servico.servico?.valorFixo || 0,
      desconto: 0,
      total: servico.valor || servico.servico?.valorFixo || 0,
      profissional: agendamento.profissional?._id || agendamento.profissional
    })),
    observacoes: `Venda originada do agendamento ${agendamentoId} - ${agendamento.titulo || ''}`
  } : null;

  return {
    ...formData,
    agendamentoOrigem: agendamento,
    isLoading: formData.isLoading || loadingAgendamento,
    isFromAgendamento: !!agendamento,
    prefillData,
    agendamentoStatus: {
      loaded: !!agendamento,
      loading: loadingAgendamento,
      error: agendamentoError,
      hasPrefill: !!prefillData,
      canCreateVenda: !!agendamento && !agendamento.financeiro?.venda
    }
  };
};

// ✅ CORREÇÃO: Hook de legado para compatibilidade
export const useVendaFormData = (options = {}) => {
  const { 
    data: profissionais, 
    isLoading: loadingProfissionais, 
    error: errorProfissionais 
  } = useProfissionaisDaConta(options);

  const { 
    data: enderecos, 
    isLoading: loadingEnderecos, 
    error: errorEnderecos 
  } = useEnderecosDaConta(options);

  const { 
    data: metodosPagamento, 
    isLoading: loadingMetodos, 
    error: errorMetodos 
  } = useMetodosPagamentoDaConta(options);

  return {
    profissionais: Array.isArray(profissionais) ? profissionais : [],
    enderecos: Array.isArray(enderecos) ? enderecos : [],
    metodosPagamento: Array.isArray(metodosPagamento) ? metodosPagamento : [],
    isLoading: loadingProfissionais || loadingEnderecos || loadingMetodos,
    error: errorProfissionais || errorEnderecos || errorMetodos,
    status: {
      profissionais: {
        loading: loadingProfissionais,
        error: errorProfissionais,
        count: profissionais?.length || 0
      },
      enderecos: {
        loading: loadingEnderecos,
        error: errorEnderecos,
        count: enderecos?.length || 0
      },
      metodosPagamento: {
        loading: loadingMetodos,
        error: errorMetodos,
        count: metodosPagamento?.length || 0
      },
      ready: !loadingProfissionais && !loadingEnderecos && !loadingMetodos,
      hasData: ((profissionais?.length || 0) > 0) && 
               ((enderecos?.length || 0) > 0) && 
               ((metodosPagamento?.length || 0) > 0)
    }
  };
};

export default useVendaFormData;